import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Avatar,
  Stack,
  Grid,
  InputAdornment,
  IconButton,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import {
  Business,
  Email,
  Phone,
  LocationOn,
  Save,
  Cancel,
  Close,
} from "@mui/icons-material";
import { useState, useEffect } from "react";

export default function ClientModal({ open, onClose, onSave, initialData }) {
  const [form, setForm] = useState({ nom: "", adresse: "", telephone: "", email: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm({
        nom: initialData.nom || "",
        adresse: initialData.adresse || "",
        telephone: initialData.telephone || "",
        email: initialData.email || "",
      });
    } else {
      setForm({ nom: "", adresse: "", telephone: "", email: "" });
    }
    // Réinitialiser saving quand le modal s'ouvre ou se ferme
    setSaving(false);
  }, [initialData, open]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    if (!form.nom) {
      return;
    }

    setSaving(true);
    onSave(form);

    setTimeout(() => {
      setSaving(false);
    }, 2000);
  };

  const getInitials = () => {
    return form.nom?.charAt(0)?.toUpperCase() || "?";
  };

  const isFormValid = !!form.nom;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
              <Business />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="600">
                {initialData ? "Modifier" : "Ajouter"} un client
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {initialData ? "Modifiez les informations du client" : "Créez un nouveau client"}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Nom"
                name="nom"
                value={form.nom}
                onChange={handleChange}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email (optionnel)"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Téléphone"
                name="telephone"
                value={form.telephone}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Adresse"
                name="adresse"
                value={form.adresse}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
          </Grid>

          {form.nom && (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                {getInitials()}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight="600">
                  Aperçu
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {form.nom}
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          startIcon={<Cancel />}
          disabled={saving}
        >
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          disabled={!isFormValid || saving}
          sx={{
            minWidth: 140,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 4,
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {saving ? (initialData ? "Modification..." : "Ajout...") : (initialData ? "Modifier" : "Ajouter")}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 